import { useState, useEffect } from 'react';
import { Modal, TextInput, Textarea, Button } from '@mantine/core';
import { createDepartment, updateDepartment } from '@mygpt/app';
import { useAdminStore } from '@/store/admin';
import { showNotification } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';

interface DepartmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: 'add' | 'edit';
  department?: any;
  onSubmit: (data: any) => void;
  parentId?: string;
}

export default function DepartmentModal({
  isOpen,
  onClose,
  mode,
  department,
  onSubmit,
  parentId,
}: DepartmentModalProps) {
  const { companyId } = useAdminStore((state) => state);
  const { t } = useTranslation();
  const title = mode === 'add' ? t('adminConsole.departmentModal.new') : t('adminConsole.departmentModal.edit');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    managerId: '',
  });

  useEffect(() => {
    if (isOpen && mode === 'edit' && department) {
      setFormData({
        name: department.name,
        code: department.code || '',
        description: department.description || '',
        managerId: department.managerId || '',
      });
    } else {
      setFormData({
        name: '',
        code: '',
        description: '',
        managerId: '',
      });
    }
  }, [isOpen, mode, department]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const apiCall = mode === 'add' ? createDepartment : updateDepartment;
    const payload: any = {
      name: formData.name,
      code: formData.code,
      description: formData.description,
      companyId: companyId,
      parentId: parentId || -1,
    };

    if (mode === 'edit' && department) {
      payload.id = department.id;
    }

    apiCall(payload)
      .then(({ data }) => {
        if (data.success) {
          onSubmit(formData);
          onClose();
        } else {
          showNotification({
            message: data.error.message,
            color: 'red',
          });
        }
      })
      .catch((error) => {
        showNotification({
          message: 'An unexpected error occurred.',
          color: 'red',
        });
        console.error('Failed to create/update department:', error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Modal opened={isOpen} onClose={onClose} title={title} centered>
      <form onSubmit={handleSubmit}>
        <TextInput
          label={t('adminConsole.departmentModal.name')}
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
          maxLength={50}
        />
        <TextInput
          label={t('adminConsole.departmentModal.code')}
          name="code"
          value={formData.code}
          onChange={handleChange}
          maxLength={10}
        />
        <Textarea
          label={t('adminConsole.departmentModal.description')}
          name="description"
          value={formData.description}
          onChange={handleChange}
          minRows={3}
          maxLength={200}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" className="ml-2" loading={loading}>
            {mode === 'add' ? t('adminConsole.departmentModal.create') : t('common.saveChanges')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
